/* Speed Junkies HQ - small shared homepage interactions. No dependencies.

   1. Carousel controller for [data-sj-carousel]
      (sections/sj-home-testimonial.liquid - brief item 9).
   2. Product cards that take the tap themselves (brief item 14):
        [data-sj-card-add]     the whole card adds to cart
                               (sections/sj-home-digital-wallpapers.liquid and
                               single-variant cards in sections/sj-look.liquid)
        [data-sj-card-select]  a card with a variant dropdown - "Add" with no
                               size chosen is stopped and the shopper is sent
                               to the dropdown (sections/sj-look.liquid)

   Adding to cart is NOT done here. The card's own form sits inside the theme's
   <product-form-component>, so a submit runs the theme's AJAX add, the cart
   drawer opens and the cart bubble updates exactly as they do everywhere else.
   This file only turns a tap on the card into a submit of that form, and stops
   a submit that should not reach the theme. With no JS at all the form still
   posts to /cart/add the plain way.

   Loaded with `defer` by every section that needs it. Duplicate script tags are
   harmless - the guard below turns the second copy into a no-op. */
(function () {
  'use strict';

  if (window.__sjHomeInteract) return;
  window.__sjHomeInteract = true;

  var SWIPE_MIN_PX = 40;    /* horizontal travel that counts as a swipe */
  var SWIPE_RATIO = 1.4;    /* |dx| has to beat |dy| by this much */
  var SLIDE_SHIFT_PX = 28;  /* how far a slide drifts while it cross-fades */
  var BUSY_MS = 1500;       /* double-submit guard on the add-to-cart cards */
  var ATTENTION_MS = 2600;  /* how long "choose a size first" stays up */

  function toArray(list) {
    return Array.prototype.slice.call(list || []);
  }

  function reducedMotion() {
    return !!(window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches);
  }

  /* ------------------------------------------------------------------
     1. CAROUSEL
     ------------------------------------------------------------------
     Markup contract (all inside one [data-sj-carousel] root):
       [data-sj-carousel-viewport]  wrapper of the slides (aria-live region)
       [data-sj-carousel-slide]     one per slide, the visible one has .is-active
       [data-sj-carousel-prev] / [data-sj-carousel-next]   arrow buttons
       [data-sj-carousel-dot]       one button per slide, in slide order

     Every slide is stacked in the same CSS grid cell, so the tallest slide
     sets the height and nothing jumps when the slide changes. This script only
     moves the .is-active class and the aria state. There is no autoplay. */
  function initCarousel(root) {
    if (!root || root.__sjCarousel) return;
    root.__sjCarousel = true;

    var slides = toArray(root.querySelectorAll('[data-sj-carousel-slide]'));
    var total = slides.length;
    if (total < 2) return;

    var viewport = root.querySelector('[data-sj-carousel-viewport]') || root;
    var prevBtn = root.querySelector('[data-sj-carousel-prev]');
    var nextBtn = root.querySelector('[data-sj-carousel-next]');
    var dots = toArray(root.querySelectorAll('[data-sj-carousel-dot]'));
    var index = 0;
    var i;

    for (i = 0; i < total; i++) {
      if (slides[i].classList.contains('is-active')) { index = i; break; }
    }

    function paint() {
      slides.forEach(function (slide, n) {
        var on = n === index;
        if (on) {
          slide.classList.add('is-active');
          slide.removeAttribute('aria-hidden');
          slide.removeAttribute('inert');
        } else {
          slide.classList.remove('is-active');
          slide.setAttribute('aria-hidden', 'true');
          slide.setAttribute('inert', '');
        }
      });
      dots.forEach(function (dot, n) {
        if (n === index) dot.setAttribute('aria-current', 'true');
        else dot.removeAttribute('aria-current');
      });
    }

    /* dir: 1 = forwards, -1 = backwards, 0 = work it out from the indexes. */
    function go(target, dir) {
      var next = ((target % total) + total) % total;
      if (next === index) return;

      var d = dir || (next > index ? 1 : -1);
      var incoming = slides[next];
      var outgoing = slides[index];
      var dotHadFocus = dots.indexOf(document.activeElement) !== -1;

      if (!reducedMotion()) {
        /* Park the incoming slide on the side it should arrive from, without
           animating that move, then let the CSS transition bring it in. */
        incoming.style.transition = 'none';
        incoming.style.setProperty('--sj-slide-x', (d > 0 ? SLIDE_SHIFT_PX : -SLIDE_SHIFT_PX) + 'px');
        void incoming.offsetWidth;
        incoming.style.transition = '';
        outgoing.style.setProperty('--sj-slide-x', (d > 0 ? -SLIDE_SHIFT_PX : SLIDE_SHIFT_PX) + 'px');
      }

      index = next;
      paint();

      /* Keep the focus ring on the current dot when the keyboard moved it. */
      if (dotHadFocus && dots[index] && typeof dots[index].focus === 'function') {
        dots[index].focus();
      }
    }

    if (prevBtn) prevBtn.addEventListener('click', function () { go(index - 1, -1); });
    if (nextBtn) nextBtn.addEventListener('click', function () { go(index + 1, 1); });

    dots.forEach(function (dot, n) {
      dot.addEventListener('click', function () { go(n, 0); });
    });

    /* Keyboard: left / right (and Home / End) while focus is anywhere inside
       the carousel - an arrow button or a dot. */
    root.addEventListener('keydown', function (ev) {
      if (ev.altKey || ev.ctrlKey || ev.metaKey || ev.shiftKey) return;
      var key = ev.key;
      if (key === 'ArrowLeft' || key === 'Left') { ev.preventDefault(); go(index - 1, -1); }
      else if (key === 'ArrowRight' || key === 'Right') { ev.preventDefault(); go(index + 1, 1); }
      else if (key === 'Home') { ev.preventDefault(); go(0, -1); }
      else if (key === 'End') { ev.preventDefault(); go(total - 1, 1); }
    });

    /* Touch swipe. The viewport has `touch-action: pan-y` in CSS, so the
       browser keeps vertical scrolling for itself (it sends pointercancel and
       we drop the gesture) and hands us the horizontal moves. Nothing here
       calls preventDefault, so page scroll is never blocked. Mouse drags are
       ignored so the quote text stays selectable on desktop. */
    var startX = 0;
    var startY = 0;
    var tracking = false;
    var pointerId = null;

    function finish(x, y) {
      var dx = x - startX;
      var dy = y - startY;
      if (Math.abs(dx) < SWIPE_MIN_PX) return;
      if (Math.abs(dx) < Math.abs(dy) * SWIPE_RATIO) return;
      if (dx < 0) go(index + 1, 1);
      else go(index - 1, -1);
    }

    if (window.PointerEvent) {
      viewport.addEventListener('pointerdown', function (ev) {
        if (ev.pointerType === 'mouse' || ev.isPrimary === false) return;
        tracking = true;
        pointerId = ev.pointerId;
        startX = ev.clientX;
        startY = ev.clientY;
      });
      viewport.addEventListener('pointerup', function (ev) {
        if (!tracking || ev.pointerId !== pointerId) return;
        tracking = false;
        finish(ev.clientX, ev.clientY);
      });
      viewport.addEventListener('pointercancel', function () { tracking = false; });
    } else {
      viewport.addEventListener('touchstart', function (ev) {
        var t = ev.touches && ev.touches.length === 1 ? ev.touches[0] : null;
        if (!t) { tracking = false; return; }
        tracking = true;
        startX = t.clientX;
        startY = t.clientY;
      }, { passive: true });
      viewport.addEventListener('touchend', function (ev) {
        var t = ev.changedTouches && ev.changedTouches[0];
        if (!tracking || !t) return;
        tracking = false;
        finish(t.clientX, t.clientY);
      }, { passive: true });
      viewport.addEventListener('touchcancel', function () { tracking = false; }, { passive: true });
    }

    /* Lets the theme editor jump to the slide whose block was selected. */
    root.__sjCarouselShow = function (slide) {
      var n = slides.indexOf(slide);
      if (n !== -1) go(n, 0);
    };

    paint();
    root.classList.add('is-ready');
  }

  document.addEventListener('shopify:block:select', function (ev) {
    var target = ev.target;
    if (!target || !target.closest) return;
    var slide = target.closest('[data-sj-carousel-slide]');
    var root = slide ? slide.closest('[data-sj-carousel]') : null;
    if (root && typeof root.__sjCarouselShow === 'function') root.__sjCarouselShow(slide);
  });

  /* ------------------------------------------------------------------
     2. PRODUCT CARDS THAT TAKE THE TAP THEMSELVES
     ------------------------------------------------------------------
     [data-sj-card-add]     a tap anywhere on the card submits that card's own
                            add-to-cart form.
     [data-sj-card-select]  the card holds a variant <select data-sj-card-variant>
                            that IS the form's id field. "Add" with nothing
                            chosen never reaches the theme: the shopper is sent
                            to the dropdown instead. A tap on the card body goes
                            to the dropdown too - it never navigates and never
                            adds, so a size is always a deliberate choice.
     The real controls stay the keyboard and screen-reader path, so the card
     itself gets no role and no tabindex. */
  var CARD = '[data-sj-card-add], [data-sj-card-select]';
  var INTERACTIVE = 'a, button, input, select, textarea, label, summary, form, [role="button"], [contenteditable="true"]';

  function cardVariantSelect(card) {
    return card.querySelector('select[data-sj-card-variant]');
  }

  function clearAttention(card) {
    clearTimeout(card.__sjAttentionTimer);
    card.removeAttribute('data-sj-attention');
    var live = card.querySelector('[data-sj-card-live]');
    if (live) live.textContent = '';
  }

  /* Send the shopper to the variant dropdown. `explain` = they tried to add
     with nothing chosen, so also outline it and say why nothing happened. */
  function promptForVariant(card, select, explain) {
    if (!select || select.disabled) return;

    if (explain) {
      var live = card.querySelector('[data-sj-card-live]');
      var message = select.getAttribute('data-sj-missing') || '';
      clearTimeout(card.__sjAttentionTimer);
      card.setAttribute('data-sj-attention', 'true');
      if (live && message) live.textContent = message;
      card.__sjAttentionTimer = setTimeout(function () { clearAttention(card); }, ATTENTION_MS);
    }

    try { select.focus(); } catch (err) { /* nothing to do */ }

    /* Opens the native picker where the browser supports it. It needs a user
       gesture - we are always inside a click / submit here - and throws when
       it is not allowed (for example inside the theme editor's iframe). */
    if (typeof select.showPicker === 'function') {
      try { select.showPicker(); } catch (err2) { /* focus alone is fine */ }
    }
  }

  function swallow(ev) {
    ev.preventDefault();
    ev.stopPropagation();
    if (typeof ev.stopImmediatePropagation === 'function') ev.stopImmediatePropagation();
  }

  function isBusy(card) {
    return !!card.__sjBusyUntil && Date.now() < card.__sjBusyUntil;
  }

  function markBusy(card) {
    card.__sjBusyUntil = Date.now() + BUSY_MS;
    card.setAttribute('data-sj-busy', 'true');
    clearTimeout(card.__sjBusyTimer);
    card.__sjBusyTimer = setTimeout(function () {
      card.__sjBusyUntil = 0;
      card.removeAttribute('data-sj-busy');
    }, BUSY_MS);
  }

  function hasTextSelection() {
    try {
      var sel = window.getSelection ? window.getSelection() : null;
      return !!sel && !sel.isCollapsed && String(sel).length > 0;
    } catch (err) {
      return false;
    }
  }

  function submitCardForm(card, form, btn) {
    if (typeof form.requestSubmit === 'function') {
      try {
        if (btn) form.requestSubmit(btn);
        else form.requestSubmit();
      } catch (err) {
        /* An engine that rejects the submitter argument. */
        if (btn) btn.click();
        else { markBusy(card); form.submit(); }
        return;
      }

      /* A card tap never clicks the button, so play the theme's own "added"
         tick on it by hand. Guarded - if the theme ever drops the method this
         simply does nothing. */
      var holder = btn && btn.closest ? btn.closest('add-to-cart-component') : null;
      if (holder && typeof holder.animateAddToCart === 'function') {
        try {
          var played = holder.animateAddToCart();
          if (played && typeof played.then === 'function') played.then(null, function () {});
        } catch (err2) { /* cosmetic only */ }
      }
      return;
    }

    if (btn) { btn.click(); return; }

    /* form.submit() fires no submit event, so set the guard by hand. */
    markBusy(card);
    form.submit();
  }

  /* One guard for every route into the form - a tap on the card, a click on
     the real button, the Enter key. It listens on window in the capture phase
     so it always runs before the theme's own document-level submit handler,
     whatever order the scripts loaded in.
       1. A card with a variant dropdown and nothing chosen: the submit is
          swallowed and the shopper is sent to the dropdown. The theme never
          sees an add with an empty id.
       2. A second submit inside BUSY_MS is swallowed.
       3. Anything else passes straight through untouched. */
  window.addEventListener('submit', function (ev) {
    var form = ev.target;
    if (!form || !form.closest) return;
    var card = form.closest(CARD);
    if (!card) return;

    var select = cardVariantSelect(card);
    if (select && !select.value) {
      swallow(ev);
      promptForVariant(card, select, true);
      return;
    }

    if (isBusy(card)) {
      swallow(ev);
      return;
    }
    markBusy(card);
  }, true);

  document.addEventListener('click', function (ev) {
    if (ev.defaultPrevented) return;

    var target = ev.target;
    if (!target || !target.closest) return;

    var card = target.closest(CARD);
    if (!card) return;

    /* Real controls inside the card look after themselves. */
    var control = target.closest(INTERACTIVE);
    if (control && card.contains(control)) return;

    /* In the theme editor a click on a card should select the block, not
       fill the merchant's cart. The button still works there. */
    if (window.Shopify && window.Shopify.designMode) return;

    if (hasTextSelection()) return;

    /* A card with a variant dropdown: the body of the card leads to the
       dropdown. With nothing chosen yet it also says why. */
    var select = cardVariantSelect(card);
    if (select) {
      promptForVariant(card, select, !select.value);
      return;
    }

    if (isBusy(card)) return;

    var form = card.querySelector('form[action*="/cart/add"]') || card.querySelector('form');
    if (!form) return;

    var btn = form.querySelector('button[type="submit"], button[name="add"], input[type="submit"]');
    if (btn && btn.disabled) return;

    submitCardForm(card, form, btn);
  });

  /* A variant was chosen: drop the "choose first" state, and when the variants
     are priced differently show the price of the one that was picked. The
     price text is rendered by Liquid onto each <option>, so no money
     formatting happens here. The selection is left alone after an add. */
  document.addEventListener('change', function (ev) {
    var select = ev.target;
    if (!select || !select.matches || !select.matches('select[data-sj-card-variant]')) return;
    var card = select.closest ? select.closest(CARD) : null;
    if (!card) return;

    if (select.value) clearAttention(card);

    var option = select.options ? select.options[select.selectedIndex] : null;
    var price = option ? option.getAttribute('data-sj-price') : '';
    var priceEl = card.querySelector('[data-sj-card-price]');
    if (price && priceEl) priceEl.textContent = price;
  });

  /* iOS Safari only sends click events up to document for elements it thinks
     are clickable. The cards have cursor:pointer in CSS, which is enough; a
     no-op listener on the card itself makes that certain. */
  function noop() {}

  function primeCard(card) {
    if (!card || card.__sjCardPrimed) return;
    card.__sjCardPrimed = true;
    card.addEventListener('click', noop);

    /* The dropdown is `required` and its form ships WITHOUT novalidate, so a
       browser running no script (or not this one) blocks an empty add with its
       own prompt. With this script running, switch that off: the submit event
       then fires, and the guard above answers it in the card's own style. */
    var select = cardVariantSelect(card);
    var form = select && select.form ? select.form : null;
    if (form) form.noValidate = true;
  }

  /* ------------------------------------------------------------------
     boot
     ------------------------------------------------------------------ */
  function init(scope) {
    var base = scope && scope.querySelectorAll ? scope : document;

    if (base !== document && base.matches) {
      if (base.matches('[data-sj-carousel]')) initCarousel(base);
      if (base.matches(CARD)) primeCard(base);
    }

    toArray(base.querySelectorAll('[data-sj-carousel]')).forEach(initCarousel);
    toArray(base.querySelectorAll(CARD)).forEach(primeCard);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { init(document); });
  } else {
    init(document);
  }

  /* The theme editor swaps a section's DOM for a fresh copy. New roots carry
     no init flag, so this picks them up without double-binding the old ones. */
  document.addEventListener('shopify:section:load', function (ev) {
    init(ev.target || document);
  });
})();
